import { Link } from 'react-router-dom';

import { useStories } from '@/features/stories/useStories';
import { useAuthStore } from '@/stores/authStore';

export function DashboardPage() {
  const mfaEnabled = useAuthStore((s) => s.mfaEnabled);
  const { data: stories, isLoading } = useStories();

  const stats = stories
    ? {
        total: stories.length,
        active: stories.filter((s) => s.status === 'active').length,
        draft: stories.filter((s) => s.status === 'draft').length,
        totalPlays: stories.reduce((sum, s) => sum + s.totalPlays, 0),
        totalNodes: stories.reduce((sum, s) => sum + s.totalNodes, 0),
      }
    : null;

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Dashboard</h1>

      {!mfaEnabled && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-center justify-between">
          <p className="text-sm text-amber-800">
            MFA is not enabled. Set up multi-factor authentication to secure your account.
          </p>
          <Link
            to="/mfa-setup"
            className="ml-4 shrink-0 text-sm font-medium text-amber-800 hover:text-amber-900 underline"
          >
            Set up MFA &rarr;
          </Link>
        </div>
      )}

      {isLoading && (
        <div className="text-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent mx-auto"></div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard label="Total Stories" value={stats.total.toString()} />
          <StatCard label="Active Stories" value={stats.active.toString()} />
          <StatCard label="Draft Stories" value={stats.draft.toString()} />
          <StatCard label="Total Plays" value={stats.totalPlays.toLocaleString()} />
        </div>
      )}

      {/* Quick actions */}
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
        <div className="flex gap-4">
          <Link
            to="/stories"
            className="rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
          >
            Manage Stories
          </Link>
        </div>
      </div>

      {/* Recent stories */}
      {stories && stories.length > 0 && (
        <div className="mt-8 rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Recent Stories</h2>
          <ul className="divide-y divide-slate-200">
            {stories.slice(0, 5).map((story) => (
              <li key={story.storyId} className="py-3">
                <Link
                  to={`/stories/${story.storyId}`}
                  className="flex items-center justify-between hover:bg-slate-50 -mx-2 px-2 rounded"
                >
                  <div>
                    <p className="font-medium text-slate-900">{story.title}</p>
                    <p className="text-sm text-slate-500">
                      {story.genre} &middot; {story.status}
                    </p>
                  </div>
                  <span className="text-sm text-slate-500">{story.totalNodes} nodes</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6">
      <dt className="text-sm font-medium text-slate-500">{label}</dt>
      <dd className="mt-2 text-3xl font-semibold text-slate-900">{value}</dd>
    </div>
  );
}
